import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const BalanceAdjustmentDialog = ({ open, onClose, calendarId, adjustmentType, onComplete }) => {
  const [newBalance, setNewBalance] = useState('');
  const [reason, setReason] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowConfirmation(true);
  };

  const handleConfirm = async () => {
    setLoading(true);

    try {
      const endpoint = adjustmentType === 'reset'
        ? `${API}/calendars/${calendarId}/reset-balance`
        : `${API}/calendars/${calendarId}/adjust-balance`;

      await axios.post(
        endpoint,
        {
          new_balance: parseFloat(newBalance),
          reason: reason.trim() || null
        },
        { withCredentials: true }
      );

      toast.success(
        adjustmentType === 'reset'
          ? '✅ Solde réinitialisé avec succès'
          : '✅ Solde ajusté avec succès'
      );

      onComplete();
      handleClose();
    } catch (error) {
      console.error('Error adjusting balance:', error);
      toast.error('Erreur lors de la modification du solde');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewBalance('');
    setReason('');
    setShowConfirmation(false);
    onClose();
  };

  if (showConfirmation) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md" data-testid="confirmation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <AlertTriangle className={`h-6 w-6 ${
                adjustmentType === 'reset' ? 'text-red-600' : 'text-amber-600'
              }`} />
              Confirmation
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {adjustmentType === 'reset' ? (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                <p className="font-semibold text-red-900 mb-2">⚠️ Attention : action irréversible</p>
                <p className="text-sm text-red-700">
                  Cette action va :
                </p>
                <ul className="list-disc list-inside text-sm text-red-700 mt-2 space-y-1">
                  <li>Supprimer toutes les opérations</li>
                  <li>Supprimer toutes les opérations récurrentes</li>
                  <li>Supprimer tous les objectifs d'épargne</li>
                  <li>Remettre le solde à {parseFloat(newBalance).toFixed(2)} €</li>
                </ul>
                <p className="text-sm text-red-700 mt-3 font-medium">
                  Veux-tu vraiment continuer ?
                </p>
              </div>
            ) : (
              <div className="bg-teal-50 border-2 border-teal-200 rounded-xl p-4">
                <p className="font-semibold text-teal-900 mb-2">Modifier le solde</p>
                <p className="text-sm text-teal-700">
                  Le solde sera ajusté à <span className="font-bold">{parseFloat(newBalance).toFixed(2)} €</span>.
                </p>
                <p className="text-sm text-teal-700 mt-2">
                  ✓ Aucune opération ne sera supprimée
                </p>
                <p className="text-sm text-teal-700">
                  ✓ L'historique reste intact
                </p>
              </div>
            )}

            {reason && (
              <div className="text-sm">
                <p className="text-gray-600 mb-1">Raison :</p>
                <p className="italic text-gray-700">"{reason}"</p>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowConfirmation(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={loading}
                className={`flex-1 ${
                  adjustmentType === 'reset'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700'
                }`}
                data-testid="confirm-adjustment-button"
              >
                {loading ? 'En cours...' : 'Confirmer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="balance-adjustment-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-blue-900">
            {adjustmentType === 'reset' ? 'Réinitialiser le solde' : 'Ajuster le solde'}
          </DialogTitle>
          <DialogDescription>
            {adjustmentType === 'reset'
              ? 'Repart à zéro avec un nouveau solde'
              : 'Modifie le solde sans toucher à l\'historique'
            }
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="new-balance">Nouveau solde (€) *</Label>
            <Input
              id="new-balance"
              type="number"
              step="0.01"
              placeholder="1000.00"
              value={newBalance}
              onChange={(e) => setNewBalance(e.target.value)}
              required
              data-testid="new-balance-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Raison (optionnel)</Label>
            <Textarea
              id="reason"
              placeholder="Ex: Correction après relevé bancaire, versement oublié..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              data-testid="reason-input"
            />
          </div>

          {adjustmentType === 'reset' && (
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-700">
                Cette action supprimera définitivement toutes les opérations enregistrées
              </p>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
              Annuler
            </Button>
            <Button
              type="submit"
              className={`flex-1 ${
                adjustmentType === 'reset'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700'
              }`}
              data-testid="submit-adjustment-button"
            >
              Continuer
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BalanceAdjustmentDialog;